import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Card,
  Grid,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import {
  GetApp as GetAppIcon,
  Share as ShareIcon,
  CheckCircle as CheckCircleIcon,
  IosShare as IosShareIcon,
  AddBoxOutlined as AddBoxOutlinedIcon,
  Speed as SpeedIcon,
  Devices as DevicesIcon,
  Restaurant as RestaurantIcon,
  ArrowForward as ArrowForwardIcon,
  TouchApp as TouchAppIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

const InstallPage: React.FC = () => {
  const navigate = useNavigate();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(
    (window as any).deferredPrompt || null
  );
  const [isInstalled, setIsInstalled] = useState(false);
  const [platform, setPlatform] = useState<'ios' | 'android' | 'desktop'>('desktop');
  const [isPromptLoading, setIsPromptLoading] = useState(false);
  const [showInstructionsDialog, setShowInstructionsDialog] = useState(false);
  const [copiedNotification, setCopiedNotification] = useState(false);

  useEffect(() => {
    // Check if already in standalone mode
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;

    if (isStandalone) {
      setIsInstalled(true);
    }

    // Platform detection
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    const ua = userAgent.toLowerCase();
    if (/iphone|ipad|ipod/i.test(ua)) {
      setPlatform('ios');
    } else if (/android/i.test(ua)) {
      setPlatform('android');
    } else {
      setPlatform('desktop');
    }

    // Check if prompt was already captured on window
    if ((window as any).deferredPrompt) {
      setDeferredPrompt((window as any).deferredPrompt);
    }

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      (window as any).deferredPrompt = e;
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    const handlePromptReady = () => {
      if ((window as any).deferredPrompt) {
        setDeferredPrompt((window as any).deferredPrompt);
      }
    };

    const handleAppInstalled = () => {
      (window as any).deferredPrompt = null;
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('pwa-prompt-ready', handlePromptReady);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('pwa-app-installed', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('pwa-prompt-ready', handlePromptReady);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('pwa-app-installed', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      try {
        setIsPromptLoading(true);
        await deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
          setIsInstalled(true);
          (window as any).deferredPrompt = null;
          setDeferredPrompt(null);
        }
      } catch (err) {
        console.error('PWA install prompt error:', err);
      } finally {
        setIsPromptLoading(false);
      }
    } else {
      // Prompt not directly available via API (e.g. iOS or browser menu required)
      setShowInstructionsDialog(true);
    }
  };

  const handleShare = async () => {
    const installUrl = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Install Atlas.Rest',
          text: 'Get the Atlas.Rest app for restaurant and cuisine discovery!',
          url: installUrl,
        });
      } catch (error) {
        // User cancelled or share failed
      }
    } else {
      try {
        await navigator.clipboard.writeText(installUrl);
        setCopiedNotification(true);
      } catch (e) {
        console.error('Clipboard copy failed', e);
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f8fafc',
        py: { xs: 4, md: 8 },
        px: 2,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={4}
          sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: 3,
            textAlign: 'center',
            bgcolor: '#ffffff',
          }}
        >
          {/* App Icon / Logo */}
          <Box sx={{ mb: 3 }}>
            <Box
              component="img"
              src="https://atlasrest.fra1.cdn.digitaloceanspaces.com/logo192.png"
              alt="Atlas.Rest Logo"
              sx={{
                width: 96,
                height: 96,
                borderRadius: '24%',
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                mb: 2,
              }}
            />
            <Typography variant="h4" component="h1" fontWeight="700" color="text.primary">
              Atlas.Rest
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 0.5 }}>
              Restaurant &amp; Cuisine Discovery
            </Typography>
          </Box>

          {/* Current Status / Action Box */}
          {isInstalled ? (
            <Alert
              icon={<CheckCircleIcon fontSize="inherit" />}
              severity="success"
              sx={{ mb: 4, borderRadius: 2, alignItems: 'center' }}
            >
              <Typography variant="body1" fontWeight="600">
                Atlas.Rest is installed on your device!
              </Typography>
              <Typography variant="body2">
                You can launch the app anytime from your home screen or app launcher.
              </Typography>
            </Alert>
          ) : platform === 'ios' ? (
            /* iOS Specific Guidance */
            <Box sx={{ mb: 4 }}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={<GetAppIcon />}
                onClick={() => setShowInstructionsDialog(true)}
                sx={{
                  py: 1.6,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2,
                  boxShadow: '0 4px 14px rgba(25, 118, 210, 0.35)',
                  mb: 2,
                }}
              >
                Install Atlas.Rest on iPhone
              </Button>

              <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: '#f1f5f9', textAlign: 'left', p: 2 }}>
                <Typography variant="subtitle2" fontWeight="700" color="primary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <TouchAppIcon fontSize="small" /> Quick Installation Steps:
                </Typography>
                <Typography variant="body2" component="div" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography component="span" fontWeight="bold">1.</Typography>
                    <Typography component="span">
                      Tap the <strong>Share</strong> button <IosShareIcon sx={{ fontSize: 18, verticalAlign: 'middle', mx: 0.5 }} /> in Safari's toolbar
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography component="span" fontWeight="bold">2.</Typography>
                    <Typography component="span">
                      Scroll down and tap <strong>Add to Home Screen</strong> <AddBoxOutlinedIcon sx={{ fontSize: 18, verticalAlign: 'middle', mx: 0.5 }} />
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography component="span" fontWeight="bold">3.</Typography>
                    <Typography component="span">
                      Tap <strong>Add</strong> in the top-right corner to finish
                    </Typography>
                  </Box>
                </Typography>
              </Card>
            </Box>
          ) : (
            /* Android / Desktop / Standard Install */
            <Box sx={{ mb: 4 }}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                disabled={isPromptLoading}
                startIcon={
                  isPromptLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <GetAppIcon />
                  )
                }
                onClick={handleInstallClick}
                sx={{
                  py: 1.6,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2,
                  boxShadow: '0 4px 14px rgba(25, 118, 210, 0.35)',
                  mb: 1.5,
                }}
              >
                {deferredPrompt ? 'Install Atlas.Rest App' : 'Add to Home Screen'}
              </Button>
              <Typography variant="caption" color="text.secondary">
                {deferredPrompt
                  ? 'Tap to install directly onto your device.'
                  : 'Fast, free, and does not require app store downloads.'}
              </Typography>
            </Box>
          )}

          {/* Value Props / Highlights */}
          <Box sx={{ my: 4, textAlign: 'left' }}>
            <Typography variant="subtitle2" color="text.secondary" fontWeight="700" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Why Install the Atlas.Rest App?
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      bgcolor: '#e3f2fd',
                      color: 'primary.main',
                      display: 'flex',
                    }}
                  >
                    <SpeedIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight="600">
                      Lightning Fast Access
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Opens instantly from your home screen with cached cuisine and restaurant data.
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      bgcolor: '#ede7f6',
                      color: '#673ab7',
                      display: 'flex',
                    }}
                  >
                    <DevicesIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight="600">
                      Clean Native Experience
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Full-screen standalone interface without browser navigation bars or distractions.
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      bgcolor: '#e8f5e9',
                      color: '#2e7d32',
                      display: 'flex',
                    }}
                  >
                    <RestaurantIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight="600">
                      Discover &amp; Bookmark
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Find top spots near you, filter by cuisines, and manage your favorites seamlessly.
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Action Links */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1.5,
              pt: 2,
              borderTop: '1px solid #e2e8f0',
            }}
          >
            <Button
              variant="outlined"
              fullWidth
              startIcon={<ArrowForwardIcon />}
              onClick={() => navigate('/')}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Open Atlas.Rest
            </Button>
            <Button
              variant="text"
              fullWidth
              startIcon={<ShareIcon />}
              onClick={handleShare}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Share Install Link
            </Button>
          </Box>
        </Paper>
      </Container>

      {/* Manual Instructions Dialog */}
      <Dialog
        open={showInstructionsDialog}
        onClose={() => setShowInstructionsDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          How to Install Atlas.Rest
        </DialogTitle>
        <DialogContent dividers>
          {platform === 'ios' ? (
            <Box>
              <Typography variant="body2" gutterBottom>
                Follow these simple steps in Safari:
              </Typography>
              <Box component="ol" sx={{ pl: 2.5, m: 0, '& li': { mb: 1.5 } }}>
                <Typography component="li" variant="body2">
                  Tap the <strong>Share</strong> button <IosShareIcon sx={{ fontSize: 18, verticalAlign: 'middle', mx: 0.5 }} /> at the bottom or top of your screen.
                </Typography>
                <Typography component="li" variant="body2">
                  Scroll down the menu and choose <strong>Add to Home Screen</strong> <AddBoxOutlinedIcon sx={{ fontSize: 18, verticalAlign: 'middle', mx: 0.5 }} />.
                </Typography>
                <Typography component="li" variant="body2">
                  Tap <strong>Add</strong> in the upper-right corner.
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" gutterBottom>
                To add Atlas.Rest to your device:
              </Typography>
              <Box component="ol" sx={{ pl: 2.5, m: 0, '& li': { mb: 1.5 } }}>
                <Typography component="li" variant="body2">
                  Open your browser's menu (the three dots <strong>⋮</strong> in Chrome or Edge).
                </Typography>
                <Typography component="li" variant="body2">
                  Select <strong>Install app</strong> or <strong>Add to Home screen</strong>.
                </Typography>
                <Typography component="li" variant="body2">
                  Confirm when prompted to install.
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInstructionsDialog(false)} sx={{ fontWeight: 600 }}>
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share / Copied Notification */}
      <Snackbar
        open={copiedNotification}
        autoHideDuration={3000}
        onClose={() => setCopiedNotification(false)}
        message="Install link copied to clipboard!"
      />
    </Box>
  );
};

export default InstallPage;
